import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Collapse,
  Flex,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useState } from 'react';

import {
  type CompanyPeekData,
  CompanyPeekTooltip,
} from '../company-details/CompanyPeekTooltip';
import type { GeoUpliftEntry } from '../hooks/useGeoSalaryUplift';
import type { GeoCompanyEntry } from '../hooks/useGeoTopCompanies';
import type { RoleLevel } from '../hooks/useResolveProfileIds';
import type { UpliftMode } from '../IndividualPotentialPage';

interface GeoUpliftTableProps {
  geoUplifts: GeoUpliftEntry[];
  /** Set of all geo IDs belonging to the user (city, msa, country, region) for exact highlight matching */
  userGeoIds: Set<number>;
  userPppFactor: number | null;
  lookOutside: boolean;
  formatCurrency: (value: number) => string;
  onExpandGeo: (geoId: number) => void;
  getCompanies: (geoId: number) => {
    companies: GeoCompanyEntry[];
    isLoading: boolean;
  };
  upliftMode: UpliftMode;
  userSalary: number;
  userRoleLevel: RoleLevel | null;
}

const formatPercent = (value: number): string => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

const getUpliftColor = (uplift: number): string => {
  if (uplift >= 0) return 'green.600';
  return 'red.500';
};

const getColAdjustedColor = (uplift: number | null): string => {
  if (uplift === null) return 'gray.400';
  if (uplift >= 0) return 'green.600';
  return 'red.500';
};

export const GeoUpliftTable = ({
  geoUplifts,
  userGeoIds,
  userPppFactor,
  lookOutside,
  formatCurrency,
  onExpandGeo,
  getCompanies,
  upliftMode,
  userSalary,
  userRoleLevel,
}: GeoUpliftTableProps) => {
  const [expandedGeoId, setExpandedGeoId] = useState<number | null>(null);

  if (geoUplifts.length === 0) return null;

  const handleRowClick = (geoId: number) => {
    if (expandedGeoId === geoId) {
      setExpandedGeoId(null);
    } else {
      setExpandedGeoId(geoId);
      onExpandGeo(geoId);
    }
  };

  return (
    <Box overflowX="auto" maxH="500px" overflowY="auto">
      <Table size="sm" variant="simple">
        <Thead position="sticky" top={0} bg="white" zIndex={1}>
          <Tr>
            {lookOutside && (
              <Th fontSize="2xs" color="gray.500" w="30px" px={1} />
            )}
            <Th fontSize="2xs" color="gray.500" w="40px">
              #
            </Th>
            <Th fontSize="2xs" color="gray.500">
              Geography
            </Th>
            <Th fontSize="2xs" color="gray.500" isNumeric>
              {upliftMode === 'percentile' ? 'At Percentile' : 'Median Salary'}
            </Th>
            <Th fontSize="2xs" color="gray.500" isNumeric>
              Uplift
            </Th>
            <Th fontSize="2xs" color="gray.500" isNumeric>
              COL-Adjusted
            </Th>
            <Th fontSize="2xs" color="gray.500" isNumeric>
              PPP
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {geoUplifts.map((geo, idx) => {
            const isUserGeo = userGeoIds.has(geo.geoId);
            const isExpanded = expandedGeoId === geo.geoId;
            const companyData = lookOutside
              ? getCompanies(geo.geoId)
              : { companies: [], isLoading: false };

            return (
              <GeoRow
                key={geo.geoId}
                geo={geo}
                rank={idx + 1}
                isUserGeo={isUserGeo}
                isExpanded={isExpanded}
                lookOutside={lookOutside}
                userPppFactor={userPppFactor}
                companies={companyData.companies}
                companiesLoading={companyData.isLoading}
                formatCurrency={formatCurrency}
                onRowClick={() => handleRowClick(geo.geoId)}
                userSalary={userSalary}
                userRoleLevel={userRoleLevel}
              />
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

// ── Individual Geo Row ──────────────────────────────────────────────

interface GeoRowProps {
  geo: GeoUpliftEntry;
  rank: number;
  isUserGeo: boolean;
  isExpanded: boolean;
  lookOutside: boolean;
  userPppFactor: number | null;
  companies: GeoCompanyEntry[];
  companiesLoading: boolean;
  formatCurrency: (value: number) => string;
  onRowClick: () => void;
  userSalary: number;
  userRoleLevel: RoleLevel | null;
}

const GeoRow = ({
  geo,
  rank,
  isUserGeo,
  isExpanded,
  lookOutside,
  userPppFactor,
  companies,
  companiesLoading,
  formatCurrency,
  onRowClick,
  userSalary,
  userRoleLevel,
}: GeoRowProps) => {
  return (
    <>
      <Tr
        bg={isUserGeo ? 'blue.50' : undefined}
        _hover={{ bg: isUserGeo ? 'blue.100' : 'gray.50' }}
        cursor={lookOutside ? 'pointer' : undefined}
        onClick={lookOutside ? onRowClick : undefined}
      >
        {lookOutside && (
          <Td px={1} w="30px">
            {isExpanded ? (
              <ChevronDownIcon color="gray.400" boxSize={3} />
            ) : (
              <ChevronRightIcon color="gray.400" boxSize={3} />
            )}
          </Td>
        )}
        <Td fontSize="xs" color="gray.400">
          {rank}
        </Td>
        <Td fontSize="xs">
          <Text fontWeight={isUserGeo ? 'bold' : 'normal'}>
            {geo.geoLongName}
            {isUserGeo && (
              <Text as="span" color="blue.500" ml={1} fontSize="2xs">
                (You)
              </Text>
            )}
          </Text>
        </Td>
        <Td fontSize="xs" isNumeric>
          {formatCurrency(geo.baseSalary)}
        </Td>
        <Td
          fontSize="xs"
          isNumeric
          color={getUpliftColor(geo.upliftPercent)}
          fontWeight="medium"
        >
          {formatPercent(geo.upliftPercent)}
        </Td>
        <Td
          fontSize="xs"
          isNumeric
          color={getColAdjustedColor(geo.colAdjustedUpliftPercent)}
          fontWeight="medium"
        >
          {geo.colAdjustedUpliftPercent !== null
            ? formatPercent(geo.colAdjustedUpliftPercent)
            : '--'}
        </Td>
        <Td fontSize="xs" isNumeric color="gray.500">
          {geo.pppFactor !== null ? geo.pppFactor.toFixed(2) : '--'}
        </Td>
      </Tr>

      {/* Expandable detail row */}
      {lookOutside && (
        <Tr>
          <Td colSpan={7} p={0} border="none">
            <Collapse in={isExpanded} animateOpacity>
              <ExpandedGeoDetail
                geo={geo}
                userPppFactor={userPppFactor}
                companies={companies}
                companiesLoading={companiesLoading}
                formatCurrency={formatCurrency}
                userSalary={userSalary}
                userRoleLevel={userRoleLevel}
              />
            </Collapse>
          </Td>
        </Tr>
      )}
    </>
  );
};

// ── Expanded Detail Panel ───────────────────────────────────────────

interface ExpandedGeoDetailProps {
  geo: GeoUpliftEntry;
  userPppFactor: number | null;
  companies: GeoCompanyEntry[];
  companiesLoading: boolean;
  formatCurrency: (value: number) => string;
  userSalary: number;
  userRoleLevel: RoleLevel | null;
}

const ExpandedGeoDetail = ({
  geo,
  userPppFactor,
  companies,
  companiesLoading,
  formatCurrency,
  userSalary,
  userRoleLevel,
}: ExpandedGeoDetailProps) => {
  return (
    <Box
      bg="gray.50"
      px={6}
      py={3}
      borderBottomWidth="1px"
      borderColor="gray.100"
    >
      <Flex gap={8} wrap="wrap">
        {/* COL / PPP Detail */}
        <Box minW="200px">
          <Text fontSize="xs" fontWeight="semibold" color="gray.700" mb={1}>
            Cost of Living
          </Text>
          {geo.colAdjustedUpliftPercent !== null && userPppFactor !== null ? (
            <>
              <Flex justify="space-between" fontSize="xs" color="gray.600">
                <Text>PPP Factor</Text>
                <Text>{geo.pppFactor?.toFixed(2) ?? '--'}</Text>
              </Flex>
              <Flex justify="space-between" fontSize="xs" color="gray.600">
                <Text>Your PPP</Text>
                <Text>{userPppFactor.toFixed(2)}</Text>
              </Flex>
              <Flex justify="space-between" fontSize="xs" mt={1}>
                <Text color="gray.700" fontWeight="medium">
                  COL-Adjusted Salary
                </Text>
                <Text
                  fontWeight="semibold"
                  color={getColAdjustedColor(geo.colAdjustedUpliftPercent)}
                >
                  {geo.colAdjustedSalary !== null
                    ? formatCurrency(geo.colAdjustedSalary)
                    : '--'}
                </Text>
              </Flex>
            </>
          ) : (
            <Text fontSize="xs" color="gray.400">
              No PPP data available for this geography
            </Text>
          )}
        </Box>

        {/* Top Companies */}
        <Box flex={1} minW="250px">
          <Text fontSize="xs" fontWeight="semibold" color="gray.700" mb={1}>
            Top Companies in {geo.geoLongName}
          </Text>
          {companiesLoading && (
            <Flex align="center" gap={2} py={1}>
              <Spinner size="xs" />
              <Text fontSize="xs" color="gray.500">
                Loading companies...
              </Text>
            </Flex>
          )}
          {!companiesLoading && companies.length === 0 && (
            <Text fontSize="xs" color="gray.400">
              No company data available
            </Text>
          )}
          {!companiesLoading &&
            companies.map((company, idx) => {
              const peekData: CompanyPeekData = {
                rcid: company.rcid,
                name: company.companyLongName || company.companyName,
                headcount: null,
                salary: company.baseSalary > 0 ? company.baseSalary : null,
                growthRate: null,
                stabilityScore: null,
                risk: null,
                sentimentOverall: null,
                linkedInUrl: null,
              };
              return (
                <Flex
                  key={company.rcid || company.companyName}
                  justify="space-between"
                  fontSize="xs"
                  py={0.5}
                >
                  <CompanyPeekTooltip
                    data={peekData}
                    userSalary={userSalary}
                    userRoleLevel={userRoleLevel}
                  >
                    <Text
                      color="gray.600"
                      noOfLines={1}
                      maxW="200px"
                      cursor="pointer"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      {idx + 1}.{' '}
                      {company.companyLongName || company.companyName}
                    </Text>
                  </CompanyPeekTooltip>
                  <Flex gap={3}>
                    <Text color="gray.700">
                      {formatCurrency(company.baseSalary)}
                    </Text>
                    <Text
                      fontWeight="medium"
                      color={getUpliftColor(company.upliftPercent)}
                      minW="60px"
                      textAlign="right"
                    >
                      {formatPercent(company.upliftPercent)}
                    </Text>
                  </Flex>
                </Flex>
              );
            })}
        </Box>
      </Flex>
    </Box>
  );
};
